import { useCallback, useEffect, useRef, useState } from 'react';

import LoadingWrapper from '../../components/LoadingWrapper';
import WorkdayCertificationsTable from '../../components/WorkdayCertifications/WorkdayCertificationsTable';
import { errorToast } from '../../components/Toasty';

import { getWorkdayCertifications, getWorkdayTopics } from '../../services/Workday/WorkdayCertificationService';

import { userCanEditWorkdayCertifications } from '../../functions/AccessHelpers';
import { isStandardWorkTrainerCert } from '../../functions/CertificationHelpers';

import EmployeeInfo from '../../types/Employee/EmployeeInfo';
import { WorkdayCertification } from '../../types/Workday/WorkdayCertification';
import { WorkdayTopic } from '../../types/Workday/WorkdayTopic';

type WorkdayCertificationsProps = {
    userData: EmployeeInfo;
    isMocked: boolean;
};

const WorkdayCertificationsPage = ({ userData, isMocked }: WorkdayCertificationsProps) => {
    const [certList, setCertList] = useState<WorkdayCertification[]>([]);
    const [allTopics, setAllTopics] = useState<WorkdayTopic[]>([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const loadData = useCallback(async () => {
        setDataLoading(true);
        try {
            const [certResult, topicResult] = await Promise.all([getWorkdayCertifications(), getWorkdayTopics()]);
            if (!mountedRef.current) return;
            if (certResult.ok && certResult.certifications) {
                setCertList(certResult.certifications.filter(c => isStandardWorkTrainerCert(c.name)));
            } else {
                errorToast(certResult.message ?? 'Error loading certificates');
            }
            if (topicResult.ok && topicResult.topics) {
                setAllTopics(topicResult.topics);
            } else {
                errorToast(topicResult.message ?? 'Error loading topics');
            }
        } finally {
            if (mountedRef.current) setDataLoading(false);
        }
    }, []);

    useEffect(() => {
        if (userData.employeeID === 0) return;
        setCanEdit(userCanEditWorkdayCertifications(userData));
        loadData();
    }, [userData, loadData]);

    return (
        <div className='standard-work-trainer-page'>
            <div className='row border-bottom mx-3'>
                <div className='col-12 text-center h2 my-3'>
                    Standard Work Trainer Certificates
                </div>
            </div>
            <div className='row pt-1 mt-1'>
                <LoadingWrapper
                    windSpinner
                    windSpinnerScale={3}
                    showLoading={dataLoading}
                    loadingMessage='Loading'
                >
                    <WorkdayCertificationsTable
                        certList={certList}
                        allTopics={allTopics}
                        userEmail={userData.email}
                        canEdit={canEdit}
                        isMocked={isMocked}
                        onSaveSuccess={loadData}
                    />
                </LoadingWrapper>
            </div>
        </div>
    );
};

export default WorkdayCertificationsPage;
